import locationPinIcon from "assets/images/location-pin-small.svg";
import {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {appointmentOption as strings} from "strings";

import Button from "components/Button";
import ConfirmAppointmentModal from "components/ConfirmAppointmentModal";
import Divider from "components/Divider";
import MobileNavigationHeader from "components/MobileNavigationHeader";
import PageTitle from "components/PageTitle";
import RadioButton from "components/RadioButton";

import * as models from "liberty-core/api/models";
import {useGetAppointmentQuestions} from "liberty-core/api/react-query/appointment";

import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./SelectAppointmentOptionPage.module.css";

type LocationState = {
  guestInfo?: models.AppointmentGuestInfo;
  selectedPatientId: string | null;
  campaignName: string;
  options: models.AppointmentCampaignOption[];
  apptCampaigns: models.AppointmentCampaign[];
  appointmentType: string;
  selectedCampaignId: string;
  selectedCampaignOptionId: string;
  insuranceInfo?: {frontImage: File; backImage: File};
  startDateTime?: string;
};

export default function SelectAppointmentOptionPage() {
  const [pharmacy] = useCurrentPharmacy();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const guestInfo = locationState.guestInfo;
  const selectedPatientId = locationState.selectedPatientId;
  const campaignName = locationState.campaignName;
  const options = locationState.options;
  const appointmentType = locationState.appointmentType;
  const apptCampaigns = locationState.apptCampaigns;
  const selectedCampaignId = locationState.selectedCampaignId;
  const selectedCampaignOptionIdPrev = locationState.selectedCampaignOptionId;
  const insuranceInfo = locationState.insuranceInfo;
  const startDateTime = locationState.startDateTime;

  const [selectedCampaignOptionId, setSelectedCampaignOptionId] = useState(
    selectedCampaignOptionIdPrev ?? "",
  );

  const [isConfirmationModalVisible, setIsConfirmationModalVisible] =
    useState(false);

  useEffect(() => {
    if (!guestInfo && !selectedPatientId) {
      navigate("/appointments", {replace: true});
    }
  }, [navigate, guestInfo, selectedPatientId]);

  const {data: appointmentQuestions, isLoading: isAppointmentQuestionsLoading} =
    useGetAppointmentQuestions(
      {
        pharmacyId: pharmacy?.id ?? 0,
        appointmentOptionId: selectedCampaignOptionId,
        startTime: startDateTime ?? "",
      },
      {enabled: !!startDateTime && selectedCampaignOptionId !== ""},
    );

  return (
    <div className={styles.container}>
      <MobileNavigationHeader
        onBack={() =>
          navigate("/appointments/select-campaign", {
            state: {
              apptCampaigns: apptCampaigns ?? [],
              guestInfo,
              selectedPatientId,
              appointmentType,
              selectedCampaignId,
              insuranceInfo,
              startDateTime,
            },
          })
        }
        alwaysShown
      />
      <PageTitle className={styles.title}>{strings.title}</PageTitle>
      <div className={styles.location}>
        <img src={locationPinIcon} alt="" />
        <span className={styles.subtitle}>{pharmacy?.name}</span>
      </div>
      <Divider className={styles.divider} />
      <div className={styles.campaignName}>{campaignName}</div>
      <Divider className={styles.divider} />
      {options
        .sort((a, b) => a.ordering - b.ordering)
        .map((campaign) => {
          return (
            <div key={campaign.id} className={styles.radioView}>
              <button
                key={campaign.id}
                onClick={() => {
                  setSelectedCampaignOptionId(campaign.id);
                }}
                className={styles.radioField}
              >
                <RadioButton
                  key={campaign.id}
                  checked={selectedCampaignOptionId === campaign.id}
                  className={styles.radioButton}
                />
                <div className={styles.radioText}>{campaign.name}</div>
                <div
                  className={
                    selectedCampaignOptionId === campaign.id
                      ? styles.radioFieldSelected
                      : undefined
                  }
                />
              </button>
              <Divider />
            </div>
          );
        })}
      <Button
        className={styles.proceed}
        disabled={
          selectedCampaignOptionId === "" || isAppointmentQuestionsLoading
        }
        onClick={() => {
          if (!startDateTime) {
            navigate("/appointments/schedule", {
              state: {
                campaignName,
                options,
                selectedPatientId,
                guestInfo,
                apptCampaigns,
                appointmentType,
                selectedCampaignId,
                selectedCampaignOptionId,
                insuranceInfo,
              },
            });
            return;
          }
          if (appointmentQuestions?.resultQuestions.length) {
            navigate("/appointments/questionnaire", {
              state: {
                campaignName: campaignName,
                options: options,
                selectedPatientId,
                guestInfo,
                apptCampaigns,
                appointmentType,
                selectedCampaignId,
                selectedCampaignOptionId: selectedCampaignOptionId,
                correlationId: appointmentQuestions.correlationId ?? "",
                resultQuestions: appointmentQuestions.resultQuestions ?? [],
                startDateTime,
                insuranceInfo,
                additionalVaccine: true,
              },
            });
          } else {
            setIsConfirmationModalVisible(true);
          }
        }}
      >
        {strings.next}
      </Button>
      <ConfirmAppointmentModal
        visible={isConfirmationModalVisible}
        onClose={() => setIsConfirmationModalVisible(false)}
        guestInfo={guestInfo}
        selectedPatientId={selectedPatientId}
        campaignName={campaignName}
        selectedCampaignOptionId={selectedCampaignOptionId}
        correlationId={appointmentQuestions?.correlationId ?? ""}
        startDateTime={startDateTime}
        responses={null}
        insuranceInfo={insuranceInfo}
        appointmentType={appointmentType}
      />
    </div>
  );
}
